import { type ReactElement, useRef, useState } from "react"
import styled from "styled-components"
import { useAccessWindowIndex } from "~/features/entitlements/AccessWindowContext"
import { useEntitlements } from "~/features/entitlements/api/useEntitlements"
import { usePocOptions } from "~/features/poc"
import { LibrarySortType } from "~/graphql/generated/graphql"
import { type SectionId, sectionStyle } from "~/shared/theme/tokens"
import { Button, CardGrid, CardRail, Section, Skeleton, StateMessage } from "~/shared/ui"
import type { LibraryListResult } from "../api/useStudyLibrary"
import {
  DEFAULT_FILTERS,
  isFiltering,
  type LibraryFilters,
  matchesAudience,
  matchesSearch,
  matchesStatus,
} from "../filters"
import { orphanCardsFor } from "../orphanEntitlements"
import { LibraryCard } from "./LibraryCard"
import { LibrarySortSelector } from "./LibrarySortSelector"

/**
 * The one section whose cards have a companion book to offer. An Ebooks card
 * asking the same question would pair with itself — both sides of the index are
 * keyed on the same stemmed title — and events have no books at all.
 */
const PAIRING_SLUG = "studies"

type LibrarySlug = Extract<SectionId, "studies" | "events" | "ebooks">

const SKELETON_KEYS = ["a", "b", "c", "d"] as const
const SKELETON_HEIGHT = "9rem"

const Footer = styled.div`
  display: flex;
  justify-content: center;
`

const SkeletonCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const ChevronButton = styled(Button).attrs({ type: "button", $variant: "outline" })`
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.sm}`};
`

/** One rail-width step, so a click advances by roughly one card. */
const scrollByRail = (rail: HTMLUListElement | null, direction: 1 | -1): void => {
  rail?.scrollBy({ left: direction * rail.clientWidth, behavior: "smooth" })
}

interface LibrarySectionProps {
  readonly title: string
  readonly slug: LibrarySlug
  readonly emptyMessage: string
  readonly useLibrary: (sortBy: LibrarySortType) => LibraryListResult
  readonly filters?: LibraryFilters
}

export const LibrarySection = ({
  title,
  slug,
  emptyMessage,
  useLibrary,
  filters = DEFAULT_FILTERS,
}: LibrarySectionProps): ReactElement => {
  const [sortBy, setSortBy] = useState<LibrarySortType>(LibrarySortType.AddedAtDesc)
  const { items: served, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useLibrary(sortBy)
  const windows = useAccessWindowIndex()
  const { layout } = usePocOptions()
  const railRef = useRef<HTMLUListElement>(null)

  /**
   * The seats this section is the only possible home for: access that has lapsed,
   * so `libraryV2` deleted the row and there is nothing to annotate. Appended
   * after the live cards rather than sorted in among them — they carry no
   * `addedAt` for the section's sort to order them by, and what has ended reads
   * better at the end of the row than interleaved with what has not.
   *
   * Shares TanStack Query's cache with `EntitlementSections`, so this is not a
   * second request. Empty with the POC facade off (`useEntitlements` is disabled),
   * and the section renders exactly as it did before.
   */
  const { items: entitlements } = useEntitlements()
  const loaded = [
    ...served,
    ...orphanCardsFor(
      slug,
      entitlements,
      served.map(item => item.title),
    ),
  ]

  /**
   * Filtered client-side, over the pages loaded so far. Status comes from the
   * matched seat — or from the card's own window when it came from one — and a
   * card with no match counts as active, the same reading a null `viewUntil` gets
   * everywhere else, so a filter never hides an item on a guess.
   */
  const items = loaded.filter(
    item =>
      matchesSearch(filters, item.title) &&
      matchesAudience(filters, item.audience) &&
      matchesStatus(filters, item.accessWindow?.state ?? windows.lookup(item.title)?.state),
  )
  const filtered = isFiltering(filters)

  const qa = `digital-library-${slug}`
  const Container = layout === "rail" ? CardRail : CardGrid

  return (
    <Section
      title={title}
      qaHook={`${qa}-section`}
      count={items.length}
      size={layout === "rail" ? "lg" : "md"}
      action={
        <Actions>
          <LibrarySortSelector id={`${qa}-sort`} value={sortBy} onChange={setSortBy} qaHook={`${qa}-sort`} />
          {layout === "rail" ? (
            <>
              <ChevronButton
                aria-label={`Scroll ${title.toLowerCase()} back`}
                data-qa-hook={`${qa}-rail-prev`}
                onClick={() => scrollByRail(railRef.current, -1)}
              >
                ‹
              </ChevronButton>
              <ChevronButton
                aria-label={`Scroll ${title.toLowerCase()} forward`}
                data-qa-hook={`${qa}-rail-next`}
                onClick={() => scrollByRail(railRef.current, 1)}
              >
                ›
              </ChevronButton>
            </>
          ) : null}
        </Actions>
      }
    >
      {isError ? (
        <StateMessage tone="error" qaHook={`${qa}-error`}>
          {`We couldn't load your ${title.toLowerCase()} right now.`}
        </StateMessage>
      ) : null}

      {isLoading ? (
        <CardGrid data-qa-hook={`${qa}-loading`}>
          {SKELETON_KEYS.map(key => (
            <SkeletonCard key={key}>
              <Skeleton $height={SKELETON_HEIGHT} />
              <Skeleton />
            </SkeletonCard>
          ))}
        </CardGrid>
      ) : null}

      {!isLoading && !isError && items.length === 0 ? (
        <StateMessage qaHook={`${qa}-empty`}>
          {filtered
            ? `No matches in ${title.toLowerCase()}${hasNextPage ? ` among the ${loaded.length} loaded so far — load more to search further` : ""}.`
            : emptyMessage}
        </StateMessage>
      ) : null}

      {items.length > 0 ? (
        <Container ref={layout === "rail" ? railRef : undefined} data-qa-hook={`${qa}-${layout}`}>
          {items.map(item => (
            <LibraryCard
              key={item.id}
              item={item}
              section={sectionStyle[slug]}
              showMaterial={slug === PAIRING_SLUG}
              qaHook={`${qa}-card`}
            />
          ))}
        </Container>
      ) : null}

      {hasNextPage ? (
        <Footer>
          <Button
            type="button"
            $variant="outline"
            data-qa-hook={`${qa}-load-more`}
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading…" : "Show more"}
          </Button>
        </Footer>
      ) : null}
    </Section>
  )
}
